/**
 * MASTER — PROCESS_STORY / «Технология».
 *
 * Explains the LOGIC of a culinary process: what changes, why, and which
 * parameters matter. This is NOT a photographic step-by-step recipe and NOT
 * an ingredient-grouped recipe — it is a reasoned, prose account of a
 * transformation. Reusable across every Digital Editions surface — nothing
 * here is Almond/product/material/route specific. All phase labels, titles
 * and parameters are author-defined; no fixed vocabulary is baked in.
 *
 * Storage:
 *   `block.content.process_story` (schema exposes it as
 *   `Optional[Dict[str, Any]]`). Legacy top-level keys of `block.content`
 *   are preserved verbatim on save.
 *
 * Shared primitives reused from HeroMaster / CropModal — no second media
 * workflow is created here. No per-phase images (per spec).
 */
import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import {
  ArrowUp, ArrowDown, Trash2, Plus, Beaker,
} from "lucide-react";
import CropModal from "@/components/editorial/CropModal";
import { ImageSlot, MediaLibraryPicker } from "@/components/editorial/HeroMaster";
import { resolveMediaUrl } from "@/lib/editorialMedia.mjs";
import { editorialImageStyle } from "@/lib/editorialReaderLayout.mjs";

const genId = (p = "id") =>
  `${p}${Date.now().toString(36)}${Math.floor(Math.random() * 1e4)}`;

function readProcess(rawContent) {
  const c = rawContent || {};
  const p = (c.process_story && typeof c.process_story === "object")
    ? c.process_story : {};

  const readImg = (img) => ({
    media_id: img?.media_id ?? null,
    url:      img?.url      ?? null,
    alt:      img?.alt      ?? "",
    focal_x:  img?.focal_x  ?? 0.5,
    focal_y:  img?.focal_y  ?? 0.5,
    zoom:     img?.zoom     ?? 1,
  });

  const phases = Array.isArray(p.phases)
    ? p.phases.map((ph) => ({
        id:        ph.id        || genId("ph"),
        label:     ph.label     || "",
        title:     ph.title     || "",
        text:      ph.text      || "",
        parameter: ph.parameter || "",
      }))
    : [];

  return {
    eyebrow:      p.eyebrow      ?? "",
    title:        p.title        ?? c.headline ?? "",
    introduction: p.introduction ?? c.lead     ?? "",
    caption:      p.caption      ?? c.caption  ?? "",
    conclusion:   p.conclusion   ?? "",
    main_image:   readImg(p.main_image),
    mobile_image: readImg(p.mobile_image),
    phases,
  };
}

// -------------------- Editor --------------------

export function ProcessStoryEditor({ block, onPatch }) {
  const p = useMemo(() => readProcess(block.content), [block.content]);
  const patch = (delta) => onPatch({
    content: { ...(block.content || {}), process_story: { ...p, ...delta } },
  });

  const [mainLib, setMainLib] = useState(false);
  const [mobileLib, setMobileLib] = useState(false);
  const [mainCrop, setMainCrop] = useState(false);
  const [mobileCrop, setMobileCrop] = useState(false);

  const setMain   = (delta) => patch({ main_image:   { ...p.main_image,   ...delta } });
  const setMobile = (delta) => patch({ mobile_image: { ...p.mobile_image, ...delta } });

  // -- ordered phases (author-defined labels / titles / parameters) --
  const setPhases = (phases) => patch({ phases });
  const addPhase = () => setPhases([
    ...p.phases, { id: genId("ph"), label: "", title: "", text: "", parameter: "" },
  ]);
  const removePhase = (i) => {
    const next = [...p.phases]; next.splice(i, 1); setPhases(next);
  };
  const movePhase = (i, dir) => {
    const next = [...p.phases]; const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setPhases(next);
  };
  const patchPhase = (i, delta) => {
    const next = [...p.phases]; next[i] = { ...next[i], ...delta };
    setPhases(next);
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-4"
         data-testid="process-editor">
      {/* LEFT — text + phases */}
      <div className="rounded-2xl bg-white border hairline p-4 space-y-4">
        <div className="text-[10px] uppercase tracking-widest opacity-70">Технология · логика процесса</div>

        <div>
          <label className="block text-[11px] opacity-60 mb-1">Метка раздела · необязательно</label>
          <Input value={p.eyebrow}
                 onChange={(e) => patch({ eyebrow: e.target.value })}
                 placeholder="ТЕХНОЛОГИЯ · РАЗБОР"
                 data-testid="process-field-eyebrow" />
        </div>
        <div>
          <label className="block text-[11px] opacity-60 mb-1">Заголовок</label>
          <Input value={p.title}
                 onChange={(e) => patch({ title: e.target.value })}
                 className="font-serif text-lg"
                 data-testid="process-field-title" />
        </div>
        <div>
          <label className="block text-[11px] opacity-60 mb-1">Введение · необязательно</label>
          <textarea rows={3} value={p.introduction}
                    onChange={(e) => patch({ introduction: e.target.value })}
                    className="w-full border rounded px-2 py-1.5 text-sm italic font-serif leading-relaxed"
                    placeholder="Короткое введение в логику процесса…"
                    data-testid="process-field-introduction" />
        </div>

        <div className="pt-2 border-t border-espresso/10">
          <div className="flex items-baseline justify-between mb-2">
            <div className="text-[10px] uppercase tracking-widest opacity-70">Фазы процесса</div>
            <button type="button" onClick={addPhase}
              className="text-[12px] inline-flex items-center gap-1 opacity-80 hover:opacity-100"
              data-testid="process-add-phase">
              <Plus className="w-3.5 h-3.5" /> Добавить фазу
            </button>
          </div>
          {p.phases.length === 0 ? (
            <div className="rounded-lg bg-cream/60 border border-dashed border-espresso/25 p-4 text-center">
              <div className="text-[12px] opacity-70">
                Ни одной фазы не добавлено. Метки, заголовки и параметры каждой фазы задаёт автор.
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {p.phases.map((ph, i) => (
                <div key={ph.id} className="rounded-lg border hairline bg-cream/40 p-3 space-y-2"
                     data-testid={`process-phase-${i}`}>
                  <div className="flex items-center gap-2">
                    <div className="text-[10px] uppercase tracking-widest opacity-60 shrink-0 font-mono">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <Input value={ph.label}
                           onChange={(e) => patchPhase(i, { label: e.target.value })}
                           placeholder="Метка фазы · авторский термин"
                           className="max-w-[240px]"
                           data-testid={`process-phase-label-${i}`} />
                    <div className="flex-1" />
                    <button type="button" onClick={() => movePhase(i, -1)} disabled={i === 0}
                      className="p-1 opacity-60 hover:opacity-100 disabled:opacity-25"
                      title="Выше" data-testid={`process-phase-up-${i}`}>
                      <ArrowUp className="w-3.5 h-3.5" />
                    </button>
                    <button type="button" onClick={() => movePhase(i, +1)} disabled={i === p.phases.length - 1}
                      className="p-1 opacity-60 hover:opacity-100 disabled:opacity-25"
                      title="Ниже" data-testid={`process-phase-down-${i}`}>
                      <ArrowDown className="w-3.5 h-3.5" />
                    </button>
                    <button type="button" onClick={() => removePhase(i)}
                      className="p-1 opacity-60 hover:opacity-100 text-red-600"
                      title="Удалить" data-testid={`process-phase-remove-${i}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <Input value={ph.title}
                         onChange={(e) => patchPhase(i, { title: e.target.value })}
                         className="font-serif"
                         placeholder="Заголовок фазы"
                         data-testid={`process-phase-title-${i}`} />
                  <textarea rows={3} value={ph.text}
                            onChange={(e) => patchPhase(i, { text: e.target.value })}
                            className="w-full border rounded px-2 py-1.5 text-sm font-serif leading-relaxed"
                            placeholder="Что происходит и почему это важно"
                            data-testid={`process-phase-text-${i}`} />
                  <Input value={ph.parameter}
                         onChange={(e) => patchPhase(i, { parameter: e.target.value })}
                         placeholder="Ключевой параметр · необязательно · авторская формулировка"
                         data-testid={`process-phase-parameter-${i}`} />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="pt-2 border-t border-espresso/10">
          <label className="block text-[11px] opacity-60 mb-1">Заключение · необязательно</label>
          <textarea rows={3} value={p.conclusion}
                    onChange={(e) => patch({ conclusion: e.target.value })}
                    className="w-full border rounded px-2 py-1.5 text-sm font-serif leading-relaxed"
                    placeholder="Краткое заключение, связывающее фазы"
                    data-testid="process-field-conclusion" />
        </div>
      </div>

      {/* RIGHT — images */}
      <div className="rounded-2xl bg-white border hairline p-4 space-y-4">
        <div className="text-[10px] uppercase tracking-widest opacity-70">Изображения</div>

        <div>
          <div className="text-[11px] opacity-60 mb-2">Основное изображение · для компьютера · необязательно</div>
          <ImageSlot
            label=""
            url={p.main_image.url}
            focalX={p.main_image.focal_x} focalY={p.main_image.focal_y} zoom={p.main_image.zoom}
            onFile={(x) => setMain({ media_id: x.id, url: x.file_url })}
            onClear={() => setMain({ media_id: null, url: null, focal_x: 0.5, focal_y: 0.5, zoom: 1 })}
            onOpenLibrary={() => setMainLib(true)}
            onOpenCrop={() => setMainCrop(true)}
            testid="process-main-image" />
          <div className="mt-2">
            <Input placeholder="Описание изображения · необязательно"
                   value={p.main_image.alt}
                   onChange={(e) => setMain({ alt: e.target.value })}
                   data-testid="process-main-alt" />
          </div>
        </div>

        <div className="pt-2 border-t border-espresso/10">
          <div className="text-[11px] opacity-60 mb-2">
            Мобильное изображение · необязательно
            <span className="ml-1 opacity-70">(если пусто — используется основное)</span>
          </div>
          <ImageSlot
            label=""
            url={p.mobile_image.url}
            focalX={p.mobile_image.focal_x} focalY={p.mobile_image.focal_y} zoom={p.mobile_image.zoom}
            onFile={(x) => setMobile({ media_id: x.id, url: x.file_url })}
            onClear={() => setMobile({ media_id: null, url: null, focal_x: 0.5, focal_y: 0.5, zoom: 1 })}
            onOpenLibrary={() => setMobileLib(true)}
            onOpenCrop={() => setMobileCrop(true)}
            testid="process-mobile-image" />
          <div className="mt-2">
            <Input placeholder="Описание изображения · необязательно"
                   value={p.mobile_image.alt}
                   onChange={(e) => setMobile({ alt: e.target.value })}
                   data-testid="process-mobile-alt" />
          </div>
        </div>

        <div className="pt-2 border-t border-espresso/10">
          <label className="block text-[11px] opacity-60 mb-1">Подпись под изображением · необязательно</label>
          <Input value={p.caption}
                 onChange={(e) => patch({ caption: e.target.value })}
                 placeholder="Короткая подпись"
                 data-testid="process-field-caption" />
        </div>
      </div>

      <MediaLibraryPicker open={mainLib} onClose={() => setMainLib(false)}
        onPick={(x) => { setMain({ media_id: x.id, url: x.file_url || x.url }); setMainLib(false); }} />
      <MediaLibraryPicker open={mobileLib} onClose={() => setMobileLib(false)}
        onPick={(x) => { setMobile({ media_id: x.id, url: x.file_url || x.url }); setMobileLib(false); }} />
      <CropModal open={mainCrop}
        url={p.main_image.url}
        focalX={p.main_image.focal_x} focalY={p.main_image.focal_y} zoom={p.main_image.zoom}
        onClose={() => setMainCrop(false)}
        onSave={({ focal_x, focal_y, zoom }) => { setMain({ focal_x, focal_y, zoom }); setMainCrop(false); }} />
      <CropModal open={mobileCrop}
        url={p.mobile_image.url}
        focalX={p.mobile_image.focal_x} focalY={p.mobile_image.focal_y} zoom={p.mobile_image.zoom}
        onClose={() => setMobileCrop(false)}
        onSave={({ focal_x, focal_y, zoom }) => { setMobile({ focal_x, focal_y, zoom }); setMobileCrop(false); }} />
    </div>
  );
}

// -------------------- Render --------------------

function ProcessImage({ img, mode = "desktop" }) {
  if (!img?.url) return null;
  return (
    <div className="w-full aspect-[4/5] overflow-hidden bg-[#E7DDCC]"
         style={{ maxHeight: mode === "mobile" ? 420 : 680 }}
         data-testid="process-image">
      <img src={resolveMediaUrl(img.url)} alt={img.alt || ""}
           className="h-full w-full"
           style={editorialImageStyle(img)} />
    </div>
  );
}

function PhaseRow({ phase, index }) {
  const num = String(index + 1).padStart(2, "0");
  return (
    <div className="py-6 first:pt-0" data-testid={`process-phase-row-${phase.id}`}>
      <div className="grid grid-cols-[auto_minmax(0,1fr)] gap-6 items-baseline">
        <div className="font-serif tabular-nums leading-none opacity-70"
             style={{ color: "#A67B4A", fontSize: "28px", letterSpacing: "-0.02em" }}>
          {num}
        </div>
        <div className="min-w-0">
          {phase.label && (
            <div className="text-[10px] tracking-[0.28em] uppercase mb-1"
                 style={{ color: "#8A6A46" }}>{phase.label}</div>
          )}
          {phase.title && (
            <h4 className="font-serif leading-[1.25]"
                style={{ fontSize: "clamp(19px, 1.5vw, 22px)", color: "#2A1D14" }}>
              {phase.title}
            </h4>
          )}
          {phase.text && (
            <p className="mt-2 font-serif text-[15px] leading-[1.7] whitespace-pre-wrap max-w-[62ch]"
               style={{ color: "#2A1D14" }}>{phase.text}</p>
          )}
          {phase.parameter && (
            <div className="mt-3 inline-flex items-baseline gap-2 py-1 px-3 rounded-full bg-cream/70 border border-espresso/10">
              <span className="text-[9px] tracking-[0.24em] uppercase opacity-70"
                    style={{ color: "#5B4326" }}>Параметр</span>
              <span className="font-serif text-[13px]" style={{ color: "#2A1D14" }}>{phase.parameter}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function ProcessStoryRender({ block, mode = "desktop" }) {
  const p = useMemo(() => readProcess(block.content), [block.content]);
  const isMobile = mode === "mobile";

  const activeImage = isMobile
    ? (p.mobile_image.url ? p.mobile_image : p.main_image)
    : p.main_image;
  const hasImage = !!(activeImage && activeImage.url);

  const phases = (p.phases || []).filter(
    (ph) => ph.label || ph.title || ph.text || ph.parameter);

  const Eyebrow = p.eyebrow ? (
    <div className="inline-flex items-center gap-2 mb-4 text-[10px] tracking-[0.28em] uppercase"
         style={{ color: "#8A6A46" }}
         data-testid="process-eyebrow">
      <Beaker className="w-3.5 h-3.5" />
      {p.eyebrow}
    </div>
  ) : null;

  const Title = p.title ? (
    <h2 className="min-w-0 break-words font-serif leading-[1.08] [overflow-wrap:anywhere]"
        style={{
          fontSize: isMobile ? "clamp(25px, 6vw, 32px)" : "clamp(30px, 3vw, 42px)",
          color: "#2A1D14",
          letterSpacing: "-0.005em",
        }}
        data-testid="process-title">
      {p.title}
    </h2>
  ) : null;

  const Intro = p.introduction ? (
    <p className="mt-4 font-serif italic text-[16px] leading-[1.6] max-w-[62ch]"
       style={{ color: "#5B4326" }}
       data-testid="process-intro">
      {p.introduction}
    </p>
  ) : null;

  const PhasesEl = phases.length ? (
    <div className={isMobile ? "mt-8 divide-y divide-espresso/10" : "mt-10 divide-y divide-espresso/10"}
         data-testid="process-phases-section">
      {phases.map((ph, i) => <PhaseRow key={ph.id} phase={ph} index={i} />)}
    </div>
  ) : null;

  const Conclusion = p.conclusion ? (
    <div className={isMobile ? "mt-8 pl-4 border-l-2" : "mt-10 pl-4 border-l-2"}
         style={{ borderColor: "#A67B4A" }}
         data-testid="process-conclusion">
      <p className="font-serif italic text-[16px] leading-[1.6] max-w-[62ch]"
         style={{ color: "#3E2E20" }}>{p.conclusion}</p>
    </div>
  ) : null;

  const Caption = p.caption ? (
    <div className="mt-3 text-[12px] italic font-serif leading-[1.5] max-w-[42ch]"
         style={{ color: "#5B4326" }}
         data-testid="process-caption">{p.caption}</div>
  ) : null;

  return (
    <section className="w-full overflow-hidden"
             style={{ background: "#EFE6D5" }}
             data-testid={`process-render-${block.id}-${mode}`}>
      <div className={`mx-auto px-6 sm:px-10 py-12 sm:py-16 ${isMobile ? "" : "max-w-[1200px]"}`}>
        {isMobile ? (
          <>
            {Eyebrow}
            {Title}
            {Intro}
            {hasImage && (
              <div className="mt-6">
                <ProcessImage img={activeImage} mode="mobile" />
              </div>
            )}
            {PhasesEl}
            {Conclusion}
            {hasImage && Caption}
          </>
        ) : hasImage ? (
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,45fr)_minmax(0,55fr)] gap-12 lg:gap-16 items-start">
            <div>
              <ProcessImage img={activeImage} mode="desktop" />
              {Caption}
            </div>
            <div>
              {Eyebrow}
              {Title}
              {Intro}
              {PhasesEl}
              {Conclusion}
            </div>
          </div>
        ) : (
          <div className="mx-auto max-w-[760px]">
            {Eyebrow}
            {Title}
            {Intro}
            {PhasesEl}
            {Conclusion}
          </div>
        )}
      </div>
    </section>
  );
}
